/* eslint-disable no-template-curly-in-string */
import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { AddonImagePosition, BillingAddonCode } from '@nx-frontend/shared/types';

const ADDONS_SETTINGS: Record<
  BillingAddonCode,
  {
    title: string;
    titleLocKey?: string;
    description: string;
    descriptionLocKey?: string;
    pricePreview: string;
    pricePreviewLocKey?: string;
    units: number;
    unitsString: string;
    unitsStringLocKey?: string;
    image: string;
    imagePosition?: AddonImagePosition;
    imageBgClassNames?: string;
    imageAddClassNames?: string;
    imageAddStyles?: Record<string, string>;
    imageBgAddStyles?: Record<string, string>;
  }
> = {
  [BillingAddonCode.AUTOMATION]: {
    title: 'Automate routine tasks without sacrificing quality.',
    titleLocKey: 'features.paywall.addons.automation.addon_popup_title',
    description:
      'Set it and forget it. Automate complex workflows with repetitive tasks to complete more projects in less time.',
    descriptionLocKey: 'features.paywall.addons.automation.addon_popup_description',
    pricePreview:
      "You'll be charged $${price} for the FuseBase Automation add-on, billed with your ${plan} plan renewal. Your next payment total is $${total}.",
    pricePreviewLocKey: 'features.paywall.addons.automation.addon_popup_price_preview',
    units: 1000,
    unitsString: '${count} runs in add-on',
    unitsStringLocKey: 'features.paywall.addons.automation.addon_popup_units_string',
    image: '/assets/img/automation-addon.png',
    imageBgClassNames: 'bg-teal-50',
    imageAddStyles: { width: '407px', maxWidth: 'unset' },
  },
  [BillingAddonCode.AI]: {
    title: 'Beat the blank page with the power of Generative AI.',
    titleLocKey: 'features.paywall.addons.generative_ai.addon_popup_title',
    description:
      "Today's charge is prorated based on the remaining days in your current billing cycle, ending on ${date}.",
    descriptionLocKey: 'features.paywall.addons.generative_ai.addon_popup_description',
    pricePreview: 'Your next payment date aligns with your ${plan} plan renewal ${date}. Total next payment $${total}.',
    pricePreviewLocKey: 'features.paywall.addons.generative_ai.addon_popup_price_preview',
    units: 0,
    unitsString: 'unlimited requests for each member',
    unitsStringLocKey: 'features.paywall.addons.generative_ai.addon_popup_units_string',
    image: '/assets/img/gen-ai-addon.png',
    imagePosition: AddonImagePosition.BOTTOM_RIGHT,
    imageBgClassNames: 'bg-purple-50',
    imageAddStyles: { width: '342px', maxWidth: 'unset' },
  },
  [BillingAddonCode.PORTALS]: {
    title: "Looks like you're growing! Grab more portals to keep up.",
    titleLocKey: 'features.paywall.addons.client_portal.addon_popup_title',
    description:
      'FuseBase is ready to scale with you and handle the expanding client base. Add more portals and continue delivering personalized experiences.',
    descriptionLocKey: 'features.paywall.addons.client_portal.addon_popup_description',
    pricePreview:
      "You'll be charged $${price} for the FuseBase ClientPortal add-on, billed with your ${plan} plan renewal. Your next payment total is $${total}.",
    pricePreviewLocKey: 'features.paywall.addons.client_portal.addon_popup_price_preview',
    units: 1,
    unitsString: '${count} Portal in add-on',
    unitsStringLocKey: 'features.paywall.addons.client_portal.addon_popup_units_string',
    image: '/assets/img/portal-addon.png',
    imagePosition: AddonImagePosition.LEFT,
    imageBgClassNames: 'bg-yellow-50',
    imageAddClassNames: 'max-h-full',
    imageAddStyles: { width: '377px', maxWidth: 'unset' },
    imageBgAddStyles: { maxHeight: '495px' },
  },
  [BillingAddonCode.TRANSCRIBE]: {
    title: 'Automate speech-to-text transcription with AI',
    titleLocKey: 'features.paywall.addons.transcribe.addon_popup_title',
    description:
      'Stop wasting time on manual transcribing. Our AI tool transforms videos into text and summaries you can edit and share with teams and clients.',
    descriptionLocKey: 'features.paywall.addons.transcribe.addon_popup_description',
    pricePreview:
      "You'll be charged $${price} for the FuseBase Transcribe add-on, billed with your ${plan} plan renewal. Your next payment total is $${total}.",
    pricePreviewLocKey: 'features.paywall.addons.transcribe.addon_popup_price_preview',
    units: 20,
    unitsString: '${count} hours in add-on',
    unitsStringLocKey: 'features.paywall.addons.transcribe.addon_popup_units_string',
    image: '/assets/img/transcribe-addon.png',
    imageBgClassNames: 'bg-blue-50',
    imageAddStyles: { width: '366px', maxWidth: 'unset' },
  },
  [BillingAddonCode.AI_ASSISTANT]: {
    title: 'The AI support you need to succeed, all in one place.',
    titleLocKey: 'features.paywall.addons.ai_assistant.addon_popup_title',
    description:
      'Get personalized assistance at every project step. Your AI assistant helps you analyze data and make data-driven decisions.',
    descriptionLocKey: 'features.paywall.addons.ai_asisstant.addon_popup_description',
    pricePreview:
      "You'll be charged $${price} for the FuseBase AI Assistant add-on, billed with your ${plan} plan renewal. Your next payment total is $${total}.",
    pricePreviewLocKey: 'features.paywall.addons.ai_assistant.addon_popup_price_preview',
    units: 1000,
    unitsString: '${count} requests in add-on ',
    unitsStringLocKey: 'features.paywall.addons.ai_assistant.addon_popup_units_string',
    image: '/assets/img/assistant-addon.png',
  },
  [BillingAddonCode.MEMBERS]: {
    title: '',
    description: '',
    pricePreview: '',
    units: 0,
    unitsString: '',
    image: '',
  },
};

export function useGetAddonTitle() {
  const t = useTranslations();
  return useCallback(
    (addon: BillingAddonCode, variables?: Record<string, string | undefined>) => {
      if (ADDONS_SETTINGS[addon].titleLocKey) {
        return t(ADDONS_SETTINGS[addon].titleLocKey, variables);
      } else {
        const template = ADDONS_SETTINGS[addon].title;
        return variables
          ? template.replace(/\${(.*?)}/g, (_, varName) => {
              const variable = varName.trim();
              return variables[variable] !== undefined ? String(variables[variable]) : '';
            })
          : template;
      }
    },
    [t]
  );
}

export function useGetAddonDescription() {
  const t = useTranslations();
  return useCallback(
    (addon: BillingAddonCode, variables?: Record<string, string | undefined>) => {
      if (ADDONS_SETTINGS[addon].descriptionLocKey) {
        return t(ADDONS_SETTINGS[addon].descriptionLocKey, variables);
      } else {
        const template = ADDONS_SETTINGS[addon].description;
        return variables
          ? template.replace(/\${(.*?)}/g, (_, varName) => {
              const variable = varName.trim();
              return variables[variable] !== undefined ? String(variables[variable]) : '';
            })
          : template;
      }
    },
    [t]
  );
}

export function useGetAddonPricePreview() {
  const t = useTranslations();
  return useCallback(
    (addon: BillingAddonCode, variables?: Record<string, number | string | undefined>) => {
      if (ADDONS_SETTINGS[addon].pricePreviewLocKey) {
        return t(ADDONS_SETTINGS[addon].pricePreviewLocKey, variables);
      } else {
        const template = ADDONS_SETTINGS[addon].pricePreview;
        return variables
          ? template.replace(/\${(.*?)}/g, (_, varName) => {
              const variable = varName.trim();
              return variables[variable] !== undefined ? String(variables[variable]) : '';
            })
          : template;
      }
    },
    [t]
  );
}

export function useGetAddonUnitsString() {
  const t = useTranslations();
  return useCallback(
    (addon: BillingAddonCode, variables?: Record<string, number | undefined>) => {
      if (ADDONS_SETTINGS[addon].unitsStringLocKey) {
        if (variables?.count) {
          variables.count *= ADDONS_SETTINGS[addon].units;
        }
        return t(ADDONS_SETTINGS[addon].unitsStringLocKey, variables);
      } else {
        const template = ADDONS_SETTINGS[addon].unitsString;
        return variables
          ? template.replace(/\${(.*?)}/g, (_, varName) => {
              const variable = varName.trim();
              return variables[variable] !== undefined
                ? String(ADDONS_SETTINGS[addon].units * (variables[variable] as number))
                : '';
            })
          : template;
      }
    },
    [t]
  );
}

export function getAddonImage(addon: BillingAddonCode) {
  const { image, imagePosition, imageBgClassNames, imageAddClassNames, imageAddStyles, imageBgAddStyles } =
    ADDONS_SETTINGS[addon];
  return { image, imagePosition, imageBgClassNames, imageAddClassNames, imageAddStyles, imageBgAddStyles };
}
